package com.siriusxm.pia.views.news

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.smithy.MarkdownText
import com.siriusxm.pia.Application
import com.siriusxm.pia.SXMUI
import com.siriusxm.pia.components.box
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Styles for the news section components.
 *
 * Defines visual styling for the news display area on the home page,
 * including the title, preview, and "See All" link.
 */
object NewsStyles : StyleSheet() {
    val newsPreview by style {
        padding(1.cssRem)
        backgroundColor(SXMUI.containerContentBackgroundColor.value())

        // Style markdown headings
        type("h1") style {
            fontSize((1.2).cssRem)
            marginTop(0.px)
            marginBottom((0.5).cssRem)
        }
        type("h2") style {
            fontSize((1.1).cssRem)
            marginTop((0.5).cssRem)
            marginBottom((0.4).cssRem)
        }
        type("h3") style {
            fontSize(1.cssRem)
            marginTop((0.4).cssRem)
            marginBottom((0.3).cssRem)
        }

        // Style lists
        type("ul") style {
            marginTop((0.5).cssRem)
            marginBottom((0.5).cssRem)
            paddingLeft((1.5).cssRem)
        }
        type("li") style {
            marginBottom((0.3).cssRem)
        }

        // Style links
        type("a") style {
            color(SXMUI.brandColor.value())
        }

        // Style paragraphs
        type("p") style {
            marginTop(0.px)
            marginBottom((0.5).cssRem)
        }
    }

    val newsContent by style {
        position(Position.Relative)
        maxHeight(14.cssRem)  // Approximately 10-12 lines
        overflow("hidden")

        // Fade out effect at the bottom
        property("mask-image", "linear-gradient(to bottom, black 70%, transparent 100%)")
        property("-webkit-mask-image", "linear-gradient(to bottom, black 70%, transparent 100%)")
    }

    val seeAllLink by style {
        display(DisplayStyle.Block)
        marginTop((0.5).cssRem)
        color(SXMUI.brandColor.value())
        cursor("pointer")
        fontSize((0.9).cssRem)

        self + hover style {
            property("text-decoration", "underline")
        }
    }

}

/**
 * Displays the news section on the home page.
 *
 * Shows the most recent news item in a minimal box (no header) with a
 * "More News..." link below to access the full news feed.
 * Uses client-side caching to reduce API calls.
 *
 * If no news items exist, nothing is rendered (the section is hidden).
 *
 * Features:
 * - Shows the latest news item with full content
 * - "More News..." link navigates to #news for full feed
 * - Hidden when no news exists
 */
@Composable
fun newsSection() {
    Style(NewsStyles)

    val news = rememberNews()
    val item = news.latestItem.value

    // Don't render anything if there's no news
    if (item == null) return

    box({
        paddedContent = false
    }) {
        Div({ classes(NewsStyles.newsPreview) }) {
            // Truncated news content with fade effect
            Div({ classes(NewsStyles.newsContent) }) {
                MarkdownText(item.markdown)
            }

            // "More News..." link to see full content
            Span({
                classes(NewsStyles.seeAllLink)
                onClick {
                    Application.navigate("news")
                }
            }) {
                Text("More News...")
            }
        }
    }
}

/**
 * Formats an ISO8601 timestamp into a human-readable date string.
 *
 * @param isoDate The ISO8601 formatted date string
 * @return A formatted date string like "Jan 15, 2026" or the original string if parsing fails
 */
internal fun formatNewsDate(isoDate: String): String {
    if (isoDate.isBlank()) return ""
    return try {
        val date = kotlin.js.Date(isoDate)
        val options = js("{}")
        options["year"] = "numeric"
        options["month"] = "short"
        options["day"] = "numeric"
        date.toLocaleDateString("en-US", options)
    } catch (e: Exception) {
        isoDate
    }
}
